import React, { useEffect, useMemo, useState } from 'react';
import { Input, Button, Textarea, Select, SelectItem, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import { Icon } from '@iconify/react';
import { FormState, useCreateSSOConnctionMutation, useDownloadSSOSAMLFileMutation, useGetFgaGroupNamesQuery, usePatchSSOConnectionMutation } from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';

interface GroupRole {
  group: string;
  value: string;
}

interface GroupRoleOption {
  key: string;
  value: string;
}

interface FormValues {
  connection_name: string;
  singining_cert: string;
  login_url: string;
  logout_url: string;
  group_roles: GroupRole[];
}

interface SSOConfigurationProps {
  refetchConnectionList: () => void;
}

const SSOConfiguration: React.FC<SSOConfigurationProps> = ({ refetchConnectionList }) => {
  const { t } = useTranslation();
  const hasSsoAccess = useHasApplicationResource('sso');
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const ssoConnectionDetails = location?.state?.ssoConnectionDetails;
  const formState = params.get('state');

  const [downloadSSOSAMLFile, { isLoading: isDownloadSSOSAMLFileLoading }] = useDownloadSSOSAMLFileMutation();
  const [createSSOConnction, { isLoading: isCreateSSOConnectionLoading }] = useCreateSSOConnctionMutation();
  const [patchSSOConnection, { isLoading: isPatchSSOConnectionLoading }] = usePatchSSOConnectionMutation();
  const [isSSOFileDownloaded, setIsSSOFileDownloaded] = useState<boolean>(false);
  const [groupDeleteModalOpen, setGroupDeleteModalOpen] = useState(false);
  const [pendingGroupRemoveIndex, setPendingGroupRemoveIndex] = useState<number | null>(null);

  const { data: groupNames = [], isLoading: isGroupNamesLoading, refetch: refetchGroupNames } = useGetFgaGroupNamesQuery();
  const groupRoleOptions = useMemo<GroupRoleOption[]>(
    () => (groupNames ?? []).map((name: string) => ({ key: name, value: name })),
    [groupNames]
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm<FormValues>({
    defaultValues: {
      connection_name: '',
      singining_cert: '',
      login_url: '',
      logout_url: '',
      group_roles: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'group_roles'
  });

  // Get current group roles to filter available options
  const currentGroupRoles = watch('group_roles');
  const availableRoles = useMemo(() => {
    return groupRoleOptions.filter(
      (option: GroupRoleOption) => !currentGroupRoles.some(role => role.group === option.key)
    );
  }, [groupRoleOptions, currentGroupRoles]);

  // Refetch group names on mount so we always get fresh data when opening create/edit connection form
  useEffect(() => {
    refetchGroupNames();
  }, [refetchGroupNames]);

  useEffect(() => {
    if (formState === FormState.UPDATE && location.state?.ssoConnectionDetails) {
      const groupRoles = ssoConnectionDetails.group_mapping.map(mapping => {
        const [group, value] = Object.entries(mapping)[0];
        return { group, value };
      });

      reset({
        connection_name: ssoConnectionDetails.connector_name,
        singining_cert: ssoConnectionDetails.signingcert,
        login_url: ssoConnectionDetails.login_url,
        logout_url: ssoConnectionDetails.logout_url,
        group_roles: groupRoles
      });
    }
  }, [formState, reset]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const openGroupDeleteModal = (index: number) => {
    setPendingGroupRemoveIndex(index);
    setGroupDeleteModalOpen(true);
  };

  const closeGroupDeleteModal = () => {
    setGroupDeleteModalOpen(false);
    setPendingGroupRemoveIndex(null);
  };

  const confirmRemoveGroup = () => {
    if (pendingGroupRemoveIndex == null) return;
    remove(pendingGroupRemoveIndex);
    closeGroupDeleteModal();
  };

  const pendingGroupName =
    pendingGroupRemoveIndex == null
      ? ''
      : watch(`group_roles.${pendingGroupRemoveIndex}.group`) ?? '';

  const handleSSOFileDownload = async () => {
    try {
      const response = await downloadSSOSAMLFile({
        connection_name: watch('connection_name')
      });

      const blob = new Blob([response.data], { type: 'text/xml; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Standard Strength Certificate (2048-bit).xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsSSOFileDownloaded(true);
    } catch (error) {
      console.log('Error:', error);
      CtyShowToast(checkType.error, 'Failed to download SSO SAML file');
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      // Validate at least one group role exists
      if (data.group_roles.length === 0) {
        CtyShowToast(checkType.error, 'At least one group role is required');
        return;
      }

      const tenantGroupMappings = data.group_roles.reduce((acc, { group, value }) => {
        acc[group] = value;
        return acc;
      }, {} as Record<string, string>);

      const group_mappings = JSON.stringify(tenantGroupMappings);

      if (formState === FormState.UPDATE) {
        const response = await patchSSOConnection({
          connectionName: data.connection_name,
          group_mappings,
        }).unwrap();
        navigate('/settings/sso');
        CtyShowToast(checkType.success, response?.message);
        refetchConnectionList();
        return;
      }

      const payload = {
        connection_name: data.connection_name,
        signing_cert: data.singining_cert,
        login_url: data.login_url,
        logout_url: data.logout_url,
        group_mappings,
      };

      const response = await createSSOConnction(payload).unwrap();
      navigate('/settings/sso');
      CtyShowToast(checkType.success, response?.message);
      refetchConnectionList();
    } catch (error) {
      CtyShowToast(checkType.error, `${t('Failed to update profile details')}: ${(error as Error)?.message || 'Something went wrong!'}`)
    }
  };

  return (
    <div className={`w-[80%] px-1 flex flex-col gap-5`}>
      <div className="flex justify-end">
        <CtyButton
          color="primary"
          isLoading={isCreateSSOConnectionLoading || isPatchSSOConnectionLoading}
          onPress={handleSubmit(onSubmit)}
          isDisabled={!hasSsoAccess}
        >
          {formState}
        </CtyButton>
      </div>

      <div className="flex flex-col gap-4">
        {/* Connection Name */}
        <div className='flex gap-8'>
          <label htmlFor="connection_name" className="w-[25%] text-default-500 text-sm">
            Connection Name<span className="text-danger">*</span>
          </label>
          <div className="flex-1">
            <Input
              id="connection_name"
              readOnly={formState === FormState.UPDATE}
              isInvalid={!!errors.connection_name}
              errorMessage={errors.connection_name?.message}
              {...register('connection_name', {
                required: 'Connection name is required'
              })}
              endContent={
                <Button
                  isDisabled={!watch('connection_name')}
                  variant="light"
                  className='border-l rounded-none w-[50%]'
                  startContent={<Icon icon="material-symbols:download" height={30} width={30} />}
                  onPress={handleSSOFileDownload}
                  isLoading={isDownloadSSOSAMLFileLoading}
                  endContent={(isSSOFileDownloaded || formState === FormState.UPDATE) && <Icon icon="icon-park-twotone:check-one" width="14" height="14" style={{ color: 'green' }} />}

                >
                  {(isSSOFileDownloaded || formState === FormState.UPDATE) ? 'Downloaded' : 'Download'}
                </Button>
              }
            />
          </div>
        </div>

        {/* Certificate */}
        <div className="flex items-start gap-8">
          <span className="text-sm text-default-500 pt-2 w-[25%]">
            Upload Certificate<span className="text-danger">*</span>
          </span>
          <div className="relative flex-1 border-1 mt-2 border-default-200 rounded-md">
            <Textarea
              id="singining_cert"
              isInvalid={!!errors.singining_cert}
              readOnly={formState === FormState.UPDATE}
              errorMessage={errors.singining_cert?.message}
              {...register('singining_cert', {
                required: 'Certificate is required'
              })}
              className="w-full resize-none"
              classNames={{
                inputWrapper: "bg-[#D0D5DD] rounded-md",
              }}
              minRows={8}
            />
          </div>
        </div>

        {/* Login URL */}
        <div className='flex gap-8'>
          <label htmlFor="login_url" className="w-[25%] text-default-500 text-sm">
            Login URL<span className="text-danger">*</span>
          </label>
          <div className="flex-1">
            <Input
              id="login_url"
              isInvalid={!!errors.login_url}
              errorMessage={errors.login_url?.message}
              readOnly={formState === FormState.UPDATE}
              {...register('login_url', {
                required: 'Login URL is required',
                pattern: {
                  value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                  message: 'Please enter a valid URL'
                }
              })}
              endContent={
                <Button
                  variant="light"
                  onClick={() => handleCopy(watch('login_url'))}
                  startContent={<Icon icon="hugeicons:copy-01" className="text-foreground" height={20} width={20} />}
                  className='border-l rounded-none'
                >
                  Copy
                </Button>
              }
            />
          </div>
        </div>

        {/* Logout URL */}
        <div className='flex gap-8'>
          <label htmlFor="logout_url" className="w-[25%] text-default-500 text-sm">
            Logout URL
          </label>
          <div className="flex-1">
            <Input
              id="logout_url"
              isInvalid={!!errors.logout_url}
              errorMessage={errors.logout_url?.message}
              readOnly={formState === FormState.UPDATE}
              {...register('logout_url', {
                pattern: {
                  value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                  message: 'Please enter a valid URL'
                }
              })}
              endContent={
                <Button
                  variant="light"
                  onClick={() => handleCopy(watch('logout_url'))}
                  startContent={<Icon icon="hugeicons:copy-01" className="text-foreground" height={20} width={20} />}
                  className='border-l rounded-none'
                >
                  Copy
                </Button>
              }
            />
          </div>
        </div>

        {/* Group Roles */}
        <div className="flex flex-col gap-3 mb-5">
          <span className="text-sm text-default-500">
            Groups<span className="text-danger">*</span>
          </span>

          {/* Add new role button */}
          {!isGroupNamesLoading && fields.length < groupRoleOptions.length && (
            <div className="flex items-center gap-3">
              <div className='flex items-center gap-2 flex-1'>
                <Select
                  id="group_role_options"
                  placeholder='Select Group'
                  className="max-w-xs max-h-10"
                  selectedKeys={new Set([])}
                  size="sm"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      append({ group: value, value });
                    }
                  }}
                >
                  {availableRoles.map((role: GroupRoleOption) => (
                    <SelectItem key={role.key} value={role.value}>
                      {role.value}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {/* Existing roles */}
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-row items-center gap-3">
              <div className='flex items-center gap-2 flex-1'>
                <Select
                  id="update_group_role_options"
                  className="max-w-xs max-h-10"
                  selectedKeys={new Set([watch(`group_roles.${index}.group`)])}
                  size="sm"
                  isDisabled
                >
                  {groupRoleOptions.map((role: GroupRoleOption) => (
                    <SelectItem key={role.key} value={role.value}>
                      {role.value}
                    </SelectItem>
                  ))}
                </Select>
                <Input
                  type="text"
                  size="sm"
                  id="group_value"
                  isInvalid={!!errors.group_roles?.[index]?.value}
                  errorMessage={errors.group_roles?.[index]?.value?.message}
                  {...register(`group_roles.${index}.value`, {
                    required: 'Value is required'
                  })}
                  className="w-full"
                  placeholder={t("Enter Value")}
                />
              </div>
              <Icon
                icon="hugeicons:delete-02"
                className="cursor-pointer"
                color="red"
                height={20}
                width={20}
                role="button"
                tabIndex={0}
                aria-label={t('Delete group')}
                onClick={() => {
                  if (formState === FormState.UPDATE) {
                    openGroupDeleteModal(index);
                  } else {
                    remove(index);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (formState === FormState.UPDATE) {
                      openGroupDeleteModal(index);
                    } else {
                      remove(index);
                    }
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <Modal
        isOpen={groupDeleteModalOpen}
        onOpenChange={(open) => {
          if (!open) closeGroupDeleteModal();
        }}
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-base">
                {t('Remove group')}
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  {t('Are you sure you want to remove this group from the mapping?')}
                </p>
                {pendingGroupName ? (
                  <p className="text-sm font-medium text-foreground mt-1">{pendingGroupName}</p>
                ) : null}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t('Cancel')}
                </Button>
                <Button color="danger" onPress={confirmRemoveGroup} isDisabled={!hasSsoAccess}>
                  {t('Remove')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default SSOConfiguration;